import { customReportsSlice } from "./CustomReportsSlice";

/*
 * Read-only data for the nine reports added 2026-11-05 (backend `api/report-data`, see the
 * backend CLAUDE.md "Reports data"). Injected into the custom reports API, so no new store
 * slice is needed. The browser computes the report numbers (features/reports/moreReports.utils.ts).
 */

export type ReportPerson = {
  id: string;
  name: string;
  color?: string | null;
  archived?: boolean;
};

export type ReportClient = {
  customerId: string;
  name: string;
  type: "organization" | "individual";
  link: string;
};

export type ProjectedCopy = {
  seriesWorkId: string;
  name: string;
  customerId: string;
  customerName: string;
  workTypeId?: string | null;
  workType: string;
  /** yyyy-mm-dd */
  dueDate: string;
  assigneeIds: string[];
  budgetHours: number;
};

export type ForecastData = {
  months: number;
  firmWide: boolean;
  projected: ProjectedCopy[];
  people: ReportPerson[];
};

export type RequestReportRow = {
  id: string;
  title: string;
  client: ReportClient;
  workId: string;
  workName: string;
  workTypeId?: string | null;
  workType: string;
  dueDate?: string | null;
  itemsTotal: number;
  itemsDone: number;
  remindersSent: number;
  maxReminders: number;
  firmNotified: boolean;
  remindersOff: boolean;
  sentAt?: string | null;
  sentByUserId?: string | null;
  sentByName: string;
  lastClientActivityAt?: string | null;
};

export type RequestReportData = {
  available: boolean;
  firmWide: boolean;
  lists: RequestReportRow[];
  completedThisYear: number;
  averageDaysToComplete?: number | null;
};

export type PaymentRow = {
  id: string;
  date: string;
  amount: number;
  method: string;
  customerId?: string | null;
  clientName: string;
  daysToPay?: number | null;
};

export type ClientOverviewRow = ReportClient & {
  email?: string | null;
  /** Individual (Contact) / organization (BusinessContact) id, for archiving. */
  contactId?: string | null;
  businessContactId?: string | null;
  owner?: ReportPerson | null;
  manager?: ReportPerson | null;
  lastActivityAt?: string | null;
  lastActivity: string;
};

export type ClientOverviewData = {
  teamAvailable: boolean;
  firmWide: boolean;
  clients: ClientOverviewRow[];
};

export type PortalClientRow = ReportClient & {
  portal: "active" | "invited" | "expired" | "none";
  invitedAt?: string | null;
  acceptedAt?: string | null;
  uploadsLast90Days: number;
  lastUploadAt?: string | null;
  lastSignedAt?: string | null;
};

export type WaitingSignature = {
  id: string;
  client: ReportClient;
  document: string;
  sentAt: string;
  daysWaiting: number;
  signBy?: string | null;
};

export type PortalReportData = {
  available: boolean;
  signaturesAvailable: boolean;
  firmWide: boolean;
  clients: PortalClientRow[];
  waiting: WaitingSignature[];
  signedThisYear: number;
  averageDaysToSign?: number | null;
};

const api = customReportsSlice.enhanceEndpoints({
  addTagTypes: ["ReportData"],
});

export const reportDataApi = api.injectEndpoints({
  endpoints: (build) => ({
    getForecastData: build.query<
      ForecastData,
      { orgId: string; months: number }
    >({
      query: ({ orgId, months }) =>
        `api/report-data/forecast?orgId=${orgId}&months=${months}`,
      providesTags: ["ReportData"],
    }),
    getRequestReportData: build.query<RequestReportData, { orgId: string }>({
      query: ({ orgId }) => `api/report-data/client-requests?orgId=${orgId}`,
      providesTags: ["ReportData"],
    }),
    getPaymentsData: build.query<
      { payments: PaymentRow[] },
      { orgId: string; from?: string | null; to?: string | null }
    >({
      query: ({ orgId, from, to }) =>
        `api/report-data/payments?orgId=${orgId}` +
        (from ? `&from=${from}` : "") +
        (to ? `&to=${to}` : ""),
      providesTags: ["ReportData"],
    }),
    getClientOverview: build.query<ClientOverviewData, { orgId: string }>({
      query: ({ orgId }) => `api/report-data/clients?orgId=${orgId}`,
      providesTags: ["ReportData"],
    }),
    getPortalReportData: build.query<PortalReportData, { orgId: string }>({
      query: ({ orgId }) => `api/report-data/portal?orgId=${orgId}`,
      providesTags: ["ReportData"],
    }),
  }),
});

export const {
  useGetForecastDataQuery,
  useGetRequestReportDataQuery,
  useGetPaymentsDataQuery,
  useGetClientOverviewQuery,
  useGetPortalReportDataQuery,
} = reportDataApi;
